import { CustomModal } from "components/custom-modal";
import { Stepper } from "components/stepper";
import { useFormFields } from "hooks/use-form-fields";
import { createModalTrigger, useModalControls } from "hooks/use-modal-controls";
import { ModalCampanhaWhatsAppSteps } from "./steps/steps";
import { CampanhaWhatsAppEndpoints } from "./utils";
import { objectToFormData } from "utils/object-methods";
import { Toast } from "components/toast";
import { openModalConfirm } from "components/modal-confirm-new";
import { PlayIcon } from "components/vectors/play-icon";
import { CloseIcon } from "components/vectors/close-icon";

export interface CampanhaWhatsAppProps {
  name?: string;
  contatoFiles?: File;
  fonesColunas?: string[];
  enabledMensagemSaudacao?: boolean;
  mensagemSaudacao?: string;
  type?: "TEXT" | "AUDIO" | "IMAGE" | "VIDEO" | "DOCUMENT";
  mensagemNormal?: string;
  midiaFiles?: File[];
  qtdEstimadaEnvioDia?: string;
  wppSessionIds?: number[];
}

interface ModalParams {
  onUpdate?: () => void;
}

const eventName = "openModalCreateCampanhaWhatsApp";

const initialValues: Partial<CampanhaWhatsAppProps> = {
  enabledMensagemSaudacao: false,
};

export function ModalCreateCampanhaWhatsApp({
  onUpdate,
}: {
  onUpdate: () => void;
}) {
  const [fields, errors, onChangeField, setFields, setErrors] =
    useFormFields<CampanhaWhatsAppProps>({ initialValues });
  const { isOpen, onClose, params } = useModalControls<ModalParams>(eventName, {
    onOpen: () => {},
    onClose: () => {
      setFields(initialValues);
      params?.onUpdate?.();
    },
  });

  const handleSubmit = async () => {
    try {
      const data = await CampanhaWhatsAppEndpoints.postCampanhasWhatsApp(
        objectToFormData(fields)
      );

      onClose();

      openModalConfirm({
        message: "Campanha criada com sucesso! Deseja iniciá-la agora?",
        onConfirm: async () => {
          await CampanhaWhatsAppEndpoints.playCampanhaWhatsApp(data.id);
          onUpdate();
        },
        variant: "normal",
        title: "Iniciar Campanha",
        confirmButtonStyle: { children: "Iniciar", leftIcon: <PlayIcon /> },
        rejectButtonStyle: { children: "Não iniciar", leftIcon: <CloseIcon /> },
      });
    } catch (e: any) {
      const errorMap = new Map<string, string>(
        Object.entries({
          type_not_informed:
            "O tipo de envio/campanha não foi especificado. Por favor, selecione uma opção válida (ex: WhatsApp, SMS, E-mail).",
          mensagem_saudacao_enabled_but_not_informed:
            "A opção de mensagem de saudação está ativada, mas o conteúdo da mensagem não foi preenchido. Por favor, insira o texto de saudação.",
          enabled_mensagem_saudacao_not_informed:
            "A mensagem de saudação foi habilitada, mas o seu conteúdo está faltando. É necessário inserir o texto da saudação.",
          fone_colunas_not_informed:
            "A coluna que contém os números de telefone não foi identificada ou informada no arquivo/lista de contatos.",
          qtd_estimada_envio_dia_must_be_between_1_and_500:
            "O valor da quantidade estimada de envios diários deve estar entre 1 e 500. Por favor, ajuste o limite.",
          midia_file_not_informed:
            "O arquivo de mídia (imagem, vídeo ou documento) que deve ser enviado não foi carregado ou especificado.",
          mensagem_normal_not_informed:
            "O conteúdo principal da mensagem de envio não foi informado. Por favor, preencha o texto da campanha.",
          wpp_sessions_not_informed:
            "Não foi selecionada ou identificada uma sessão ativa do WhatsApp para realizar o envio da campanha.",
        })
      );
      Toast({
        title: "Erro ao criar campanha",
        description:
          errorMap.get(e.response?.data?.code) ||
          "Erro desconhecido ao criar campanha",
        status: "error",
      });
    } finally {
    }
  };

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={onClose}
      modalTitle="Campanha WhatsApp"
      size="3xl"
      closeOnOverlayClick={false}
    >
      <Stepper
        steps={[
          { title: "Dados da Campanha" },
          { title: "Mensagem" },
          { title: "Envio" },
          { title: "Revisar" },
        ]}
        stepperContainerProps={{ pb: "20px" }}
        children={(controls) => (
          <ModalCampanhaWhatsAppSteps
            onChangeField={onChangeField}
            fields={fields}
            onSubmit={handleSubmit}
            {...controls}
          />
        )}
      />
    </CustomModal>
  );
}

export const openModalCreateCampanhaWhatsapp =
  createModalTrigger<ModalParams>(eventName);
